import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3, BookOpen, LayoutGrid, Network, Search, Server, Target, Users, UsersRound, X,
} from 'lucide-react';
import styles from './AdminMenu.module.css';
import { adminCategoryLabel, menuStructureFor } from '../Header/SettingsMenu';
import { useAuth } from '@/contexts/shared/AuthContext';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { IconTile, type Tint } from '@/components/ds';

import { tr } from '@/i18n';
const CATEGORY_META: Record<string, { icon: React.ElementType; tint: Tint }> = {
  Platform: { icon: LayoutGrid, tint: 'violet' },
  Users: { icon: Users, tint: 'sky' },
  Hierarchy: { icon: Network, tint: 'mint' },
  Social: { icon: UsersRound, tint: 'pink' },
  Learning: { icon: BookOpen, tint: 'lime' },
  Performance: { icon: Target, tint: 'coral' },
  Analytics: { icon: BarChart3, tint: 'sun' },
  Server: { icon: Server, tint: 'neutral' },
};

export const AdminMenu: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  // A course creator who isn't an administrator sees only the course pages they can use.
  const { canOpen } = useAdminAccess();
  const menu = useMemo(
    () => menuStructureFor(user)
      .map((g) => ({ ...g, items: g.items.filter((i) => canOpen(i.href)) }))
      .filter((g) => g.items.length > 0),
    [user, canOpen],
  );
  const initial = useMemo(() => {
    const hit = menu.find((g) => g.items.some((i) => location.pathname.startsWith(i.href)));
    return hit?.category ?? menu[0]?.category ?? '';
  }, [location.pathname, menu]);
  const [active, setActive] = useState(initial);
  const [q, setQ] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => { searchRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const term = q.trim().toLowerCase();
  const results = term
    ? menu.flatMap((g) =>
        g.items
          .filter((i) => i.title.toLowerCase().includes(term) || i.description.toLowerCase().includes(term))
          .map((i) => ({ ...i, category: g.category })),
      )
    : (menu.find((g) => g.category === active)?.items ?? []).map((i) => ({ ...i, category: active }));

  return (
    <div className={styles.root} data-topbar-pop>
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />
      <div className={styles.panel} role="dialog" aria-label={tr("ui.admin_settings", "Admin settings")}>
        <div className={styles.head}>
          <h2 className={styles.title}>{tr("ui.admin", "Admin")}</h2>
          <label className={styles.search}>
            <Search size={18} aria-hidden="true" />
            <input
              ref={searchRef}
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tr("ui.search_settings", "Search settings")}
              aria-label={tr("ui.search_admin_settings", "Search admin settings")}
            />
          </label>
          <button type="button" className={styles.close} onClick={onClose} aria-label={tr("ui.close_admin_menu", "Close admin menu")}><X size={20} /></button>
        </div>

        <div className={styles.body}>
          <nav className={styles.rail} aria-label={tr("ui.admin_sections", "Admin sections")}>
            {menu.map((g) => {
              const meta = CATEGORY_META[g.category] ?? { icon: LayoutGrid, tint: 'neutral' as Tint };
              const Icon = meta.icon;
              const on = !term && g.category === active;
              return (
                <button
                  key={g.category}
                  type="button"
                  className={`${styles.railBtn} ${on ? styles.railOn : ''}`}
                  aria-pressed={on}
                  onClick={() => { setQ(''); setActive(g.category); }}
                >
                  <IconTile tint={meta.tint} size={32} radius="var(--r-control-sm)"><Icon size={17} aria-hidden="true" /></IconTile>
                  <span>{adminCategoryLabel(g.category)}</span>
                  <span className={styles.railCount}>{g.items.length}</span>
                </button>
              );
            })}
          </nav>

          <div className={styles.grid}>
            {results.length === 0 && <p className={styles.empty}>{tr("ui.no_settings_match", "No settings match “")}{q}”.</p>}
            {results.map((item) => {
              const meta = CATEGORY_META[item.category] ?? { icon: LayoutGrid, tint: 'neutral' as Tint };
              const Icon = meta.icon;
              const current = location.pathname.startsWith(item.href);
              return (
                <Link key={item.href + item.title} to={item.href} className={`${styles.card} ${current ? styles.cardOn : ''}`} onClick={onClose}>
                  <IconTile tint={meta.tint} size={40}><Icon size={20} aria-hidden="true" /></IconTile>
                  <span className={styles.cardText}>
                    <span className={styles.cardTitle}>{item.title}</span>
                    <span className={styles.cardDesc}>{item.description}</span>
                    {term && <span className={styles.cardCat}>{adminCategoryLabel(item.category)}</span>}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
